import type { PhraseRef } from './types.ts';

/**
 * Structured quick phrases (GDD §13.1). A phrase is an id plus typed parameters, so the
 * client can render it in any language and bots can speak and read it without parsing text.
 * Text lives in the translation files under `phrases.<id>`.
 */

export type ParamSpec =
  { kind: 'int'; min: number; max: number } | { kind: 'enum'; values: readonly string[] };

export interface PhraseDef {
  params: readonly ParamSpec[];
}

const DIRS8 = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'] as const;
const DIRS4 = ['n', 'e', 's', 'w'] as const;
const ROLE_NAMES = ['mastermind', 'locksmith', 'hacker', 'lookout', 'driver'] as const;
const int = (min: number, max: number): ParamSpec => ({ kind: 'int', min, max });
const en = (values: readonly string[]): ParamSpec => ({ kind: 'enum', values });
/** A player seat, 1-based, so phrases do not carry ids. */
const SEAT = int(1, 5);

/** Panel manual vocabulary: shared with the bots so that both sides agree on the spelling. */
export const PANEL_COLORS = ['red', 'blue', 'yellow', 'black', 'white', 'green'] as const;
export const PANEL_CONDITIONS = ['color', 'odd', 'even', 'first', 'last', 'twoPlus'] as const;
export const PANEL_ACTIONS = ['cut', 'connect', 'leave'] as const;
/** Password clue kinds, in the order the phrase uses. */
export const PASSWORD_CLUE_KINDS = ['category', 'first', 'last', 'contains', 'excludes'] as const;
export const PASSWORD_CATEGORIES = ['place', 'tool', 'person', 'loot', 'vice'] as const;

export const PHRASES = {
  // General
  ready: { params: [] },
  wait: { params: [] },
  go: { params: [] },
  danger: { params: [] },
  needRole: { params: [en(ROLE_NAMES)] },
  done: { params: [] },
  repeat: { params: [] },
  dontKnow: { params: [] },
  // Lock
  lockPinHeight: { params: [int(1, 7), int(1, 5)] },
  lockNextPin: { params: [int(1, 7)] },
  // Safe: one phrase per kind of line on the owner's profile, so a profile can be read out in full
  safeDigit: { params: [int(1, 5), int(0, 9)] },
  safeClue: { params: [int(0, 9)] },
  safeNo: { params: [int(0, 9)] },
  safeSum: { params: [int(0, 45)] },
  safeParity: { params: [int(1, 5), en(['even', 'odd'])] },
  safeGreater: { params: [int(1, 5), int(1, 5)] },
  safeAttempt: { params: [int(0, 99999), int(0, 5), int(0, 5)] },
  // Panel: a manual rule is "number, condition, colour, action"; the serial letter rule has
  // its own phrase because it needs a letter
  panelSerial: { params: [int(0, 999)] },
  panelWire: {
    params: [
      en(['red', 'blue', 'yellow', 'black', 'white', 'green']),
      en(['cut', 'connect', 'leave']),
    ],
  },
  panelRule: {
    params: [int(1, 8), en(PANEL_CONDITIONS), en([...PANEL_COLORS, 'any']), en(PANEL_ACTIONS)],
  },
  panelRuleLetter: { params: [int(1, 8), en(PANEL_COLORS), int(0, 25), en(PANEL_ACTIONS)] },
  // Cameras
  camAvoid: { params: [int(1, 4), en(DIRS8)] },
  camNow: { params: [int(1, 4), en(DIRS8)] },
  // Laser
  laserMirror: { params: [int(1, 5), en(['slash', 'backslash'])] },
  laserEnds: { params: [int(0, 8), int(0, 8)] },
  // Password
  passClue: { params: [en(PASSWORD_CLUE_KINDS), int(0, 25)] },
  passTry: { params: [int(1, 16)] },
  // Corridor
  corGuard: { params: [int(1, 3), en(DIRS4)] },
  corHold: { params: [SEAT] },
  corShadow: { params: [int(0, 6), int(0, 6)] },
  // Escape
  escReinforce: { params: [en(['left', 'right', 'top', 'bottom']), int(0, 60)] },
  escMove: { params: [int(0, 16)] },
  // Vault
  vaultPos: { params: [int(1, 5), int(0, 7)] },
  vaultConfirm: { params: [] },
  // Radio: only the driver hears the bulletins, so the driver has to pass them on
  bulletin: { params: [en(['suspiciousGuard', 'interference', 'patrolEarly'])] },
  // Hideout
  suspect: { params: [SEAT] },
  trust: { params: [SEAT] },
  sawWeird: { params: [int(0, 13)] },
} as const satisfies Record<string, PhraseDef>;

export type PhraseId = keyof typeof PHRASES;

export function isPhraseId(id: string): id is PhraseId {
  return Object.prototype.hasOwnProperty.call(PHRASES, id);
}

/** Checks id, arity, integer ranges and enum members. */
export function validatePhrase(ref: PhraseRef): string | null {
  if (!isPhraseId(ref.id)) return `unknown phrase ${ref.id}`;
  const def: PhraseDef = PHRASES[ref.id];
  if (ref.params.length !== def.params.length) return 'wrong number of parameters';
  for (let i = 0; i < def.params.length; i++) {
    const spec = def.params[i] as ParamSpec;
    const v = ref.params[i];
    if (spec.kind === 'int') {
      if (typeof v !== 'number' || !Number.isInteger(v) || v < spec.min || v > spec.max) {
        return `parameter ${i} out of range`;
      }
    } else if (typeof v !== 'string' || !spec.values.includes(v)) {
      return `parameter ${i} is not one of ${spec.values.join('|')}`;
    }
  }
  return null;
}
